import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import LayersIcon from '@mui/icons-material/Layers';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { Button, IconButton } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import "react-leaflet-fullscreen/styles.css";

import classNames from 'classnames';
import { MapDataContext } from '../context/map-data';
import styles from "../styles/components/on-demand-layers.module.scss";
import { EditableGridBoundsState } from './rectangle';

export const API_BASE = import.meta.env.VITE_API_BASE || '';

export const OnDemandsLayers = () => {
  const [openActionsForId, setOpenActionsForId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [clearAll, setClearAll] = useState<boolean>(false)

  const {
    dataSourceModel,
    gridPoints,
    activePointId,
    setGridPoints,
    setActivePointId,
    currentMapCenter,
    getFeatureLocation,
  } = useContext(MapDataContext);

  const enableForThisDataSources = useMemo(() => {
    return !!dataSourceModel?.key?.includes('ondemand');
  }, [dataSourceModel]);

  useEffect(() => {
    setClearAll(false)
  }, [])

  const snap = (value: number, step: number) =>
  +(Math.round(value / step) * step).toFixed(6)

  const makeBoundsAroundCenter = (
    centerLat: number,
    centerLon: number
  ): EditableGridBoundsState => {
    // Centre point keeps its raw map location. Only bounds align to the map grid.
    const pointLat = +centerLat.toFixed(6)
    const pointLon = +centerLon.toFixed(6)

    const gridLat = snap(pointLat, 0.1)
    const gridLon = snap(pointLon, 0.1)

    const outerRows = 6
    const outerCols = Math.floor(
      outerRows / Math.cos((gridLat * Math.PI) / 180)
    )

    const outerLatMin = snap(gridLat - outerRows / 2, 1)
    const outerLonMin = snap(gridLon - outerCols / 2, 1)

    const outerBounds = {
      latMin: outerLatMin,
      latMax: +(outerLatMin + outerRows).toFixed(6),
      lonMin: outerLonMin,
      lonMax: +(outerLonMin + outerCols).toFixed(6),
    }

    const innerRows = 1
    const innerCols = 2

    const innerBounds = {
      latMin: snap(gridLat - innerRows / 2, 0.1),
      latMax: snap(gridLat + innerRows / 2, 0.1),
      lonMin: snap(gridLon - innerCols / 2, 0.1),
      lonMax: snap(gridLon + innerCols / 2, 0.1),
    }

    return {
      outerBounds,
      innerBounds,
      centerLat: pointLat,
      centerLon: pointLon,
      centerPoints: [
        {
          id: `point-${Date.now()}`,
          lat: pointLat,
          lon: pointLon,
        },
      ],
    }
  }

  const handleAddLayer = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setClearAll(false)

    const location = getFeatureLocation?.()
    const cenLat = location?.cenLat ?? currentMapCenter?.location.lat
    const cenLon = location?.cenLon ?? currentMapCenter?.location.lng
    // const cenLat = currentMapCenter?.location.lat
    // const cenLon = currentMapCenter?.location.lng

    if (
      cenLat === undefined ||
      cenLon === undefined ||
      cenLat === null ||
      cenLon === null
    ) return

    const id = `grid-${Date.now()}`
    const bounds = makeBoundsAroundCenter(Number(cenLat), Number(cenLon))

    setGridPoints((prev: any[] = []) => [
      ...prev,
      {
        id,
        cenLat,
        cenLon,
        hidden: false,
        bounds,
      },
    ])

    setConfirmDeleteId(null)
    setActivePointId(id)
    setOpenActionsForId(id)
  }

  const handleSelectLayer = (
    e: React.SyntheticEvent,
    id: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setClearAll(false)

    if (id === activePointId) {
      setOpenActionsForId((prev) => prev === id ? null : id);
      return;
    }

    setActivePointId(id);
    setOpenActionsForId(null);

    setConfirmDeleteId(null);

    if (id === activePointId) {
      setOpenActionsForId((prev) => prev === id ? null : id);
      return;
    } else {
      setGridPoints((prev: any[] = []) =>
        prev.map((point) => {
          if (point.id !== id) return point;

          return {
            ...point,
            hidden: false,
          };
        })
      );
    }
  };

  const handleToggleHidden = (
    e: React.SyntheticEvent,
    id: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setClearAll(false)
    setGridPoints((prev: any[] = []) =>
      prev.map((point) => {
        if (point.id !== id) return point;

        return {
          ...point,
          hidden: !point.hidden,
        };
      })
    );
  };

  const handleRemoveLayer = (
    e: React.SyntheticEvent,
    id: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setClearAll(false)
    // First click: arm delete
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }

    // Second click: actually delete
    setGridPoints((prev: any[] = []) => {
      const next = prev.filter((point) => point.id !== id);

      if (activePointId === id) {
        setActivePointId(next[0]?.id ?? null);
      }

      return next;
    });

    setConfirmDeleteId(null);
    setOpenActionsForId(null);
  };

  if (!enableForThisDataSources) {
    return null;
  }

  return (
    <div className={styles.layerTabs}>
      {(gridPoints || []).map((point: any) => {
        const isActive = point.id === activePointId;
        const isActionsOpen = openActionsForId === point.id;

        return (
          <div
            key={point.id}
            className={classNames(styles.layerTabItem, {
              [styles.actionsOpen]: isActionsOpen,
            })}
          >
            <Button
              onClick={(e) => handleSelectLayer(e, point.id)}
              size="small"
              variant="contained"
              className={classNames(styles.layerTab, {
                [styles.active]: isActive,
              })}
            >
              {point.hidden ? <LayersClearIcon /> : <LayersIcon />}
            </Button>

            <div className={styles.layerActions}>
              <IconButton
                size="small"
                onClick={(e) => handleToggleHidden(e, point.id)}
              >
                {point.hidden ? <VisibilityOffIcon />: <VisibilityIcon />}
              </IconButton>

              <IconButton
                size="small"
                onClick={(e) => handleRemoveLayer(e, point.id)}
                className={classNames(styles.deleteButton, {
                  [styles.confirmDelete]: confirmDeleteId === point.id,
                })}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </div>
        );
      })}

      <Button
        onClick={handleAddLayer}
        variant="contained"
        size="small"
        className={styles.addLayerButton}
      >
        <LibraryAddIcon />
      </Button>
      {(gridPoints && gridPoints.length > 0) && 
        <Button
          onClick={() => {
            if (clearAll) {
              setGridPoints([])
            } else {
              setClearAll(true)
            }
          }}
          variant="contained"
          size="small"
          className={classNames(styles.clearButton, {
            [styles.confirmDelete]: clearAll,
          })}
        >
          <ClearIcon />
        </Button>
      }
    </div>
  );
};